<template>
  <div class="tm-person">
    <div class="tm-person__avatar">
      <slot name="avatar">
        <tm-avatar
          v-bind="placeholderProps"
          :url="avatarUrl"
          :name="avatarName ?? name"
          :color="avatarColor"
          :size="avatarSize"
          :placeholder-custom-size="avatarPlaceholderCustomSize"
          :custom-size="avatarCustomSize"
          :exact-color="avatarExactColor"
          :custom-icon-class="avatarCustomIconClass"
        >
          <template
            v-if="$slots['avatar-status']"
            v-slot:status
          >
            <slot name="avatar-status" />
          </template>
        </tm-avatar>
      </slot>
    </div>
    <tm-conditional-link
      v-if="name || $slots.name"
      tag="span"
      class="tm-person__name truncate"
      :class="{
        'font-weight-semi-bold': semiBold,
        'blue-on-hover': to,
      }"
      :style="{ 'max-width': maxWidth }"
      :is-blank="isBlank"
      :to="to"
    >
      <slot
        name="name"
        v-bind="{ name }"
      >
        <person-name-highlight
          :name="name"
          :search="searchQuery"
        />
      </slot>
    </tm-conditional-link>
  </div>
</template>

<script lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { computed, defineComponent, type PropType } from '@/composition/vue/compositionApi'
import TmAvatar from '@/components/shared/TmAvatar.vue'
import type { AvatarSize, SizeProp } from '@/components/shared/types'
import PersonNameHighlight from '@/components/shared/misc/PersonNameHighlight.vue'
import TmConditionalLink from '@/components/shared/TmConditionalLink.vue'
import type { AllAvatarColors } from '@/constants/avatar'
import type { IconName } from '@/assets/icons/icons'
import type { StylesheetSizeProp } from '@/types'

export default defineComponent({
  components: {
    TmConditionalLink,
    PersonNameHighlight,
    TmAvatar,
  },
  props: {
    name: {
      type: String,
    },
    avatarName: {
      type: String,
    },
    avatarUrl: {
      type: String,
    },
    avatarColor: {
      type: String as PropType<AllAvatarColors>,
    },
    avatarIcon: {
      type: String as PropType<IconName>,
    },
    avatarSize: {
      type: String as SizeProp<AvatarSize>,
      default: 'medium',
    },
    avatarCustomSize: {
      type: String,
    },
    avatarPlaceholderCustomSize: {
      type: String,
    },
    avatarExactColor: Boolean,
    avatarCustomIconClass: {
      type: String,
    },
    to: {
      type: Object as PropType<RouteLocationRaw>,
    },
    isBlank: {
      type: Boolean,
    },
    semiBold: {
      type: Boolean,
    },
    searchQuery: {
      type: String,
    },
    maxWidth: {
      type: String as PropType<StylesheetSizeProp>,
    },
  },
  setup(props) {
    const placeholderProps = computed(() => {
      const obj: Record<string, any> = {}
      if (props.avatarIcon) {
        obj.placeholderType = 'icon'
        obj.customIcon = props.avatarIcon
      }
      return obj
    })

    return {
      placeholderProps,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins';

.tm-person {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: inherit;
  @include overflow-hidden-with-clip-margin;

  &__name {
    line-height: 20px;
    font-weight: inherit;
    color: $emphasize;
    overflow: hidden;
    text-overflow: ellipsis;

    a:hover &,
    .blue-on-hover:hover & {
      color: $blue;
    }
  }
}
</style>
